// U4 intent chips: when a reply hinges on a decision, the draft's versions are labelled by intent
// ("Yes, Thursday works", "Can't make it") and shown as chips above it. Picking one swaps in that
// version (the same `variants` and `v` key as before; the chips are just their labels). Arrow keys
// move between chips; the palette's "Pick how to answer" focuses them.
import type { KeyboardEvent } from "react";
import { cn } from "@web/lib/cn";
import { Kbd } from "@web/ui/Kbd";

export interface IntentChipsProps {
  cardId: string;
  labels: readonly string[];
  index: number;
  onPick: (index: number) => void;
  disabled: boolean;
}

/** Focuses the chosen chip of a card's intent chips (the palette command). */
export function focusIntentChips(cardId: string): boolean {
  const group = document.querySelector<HTMLElement>(`[data-intent-chips="${CSS.escape(cardId)}"]`);
  const chip =
    group?.querySelector<HTMLButtonElement>('[aria-checked="true"]') ??
    group?.querySelector<HTMLButtonElement>("button");
  chip?.focus();
  return !!chip;
}

export function IntentChips({ cardId, labels, index, onPick, disabled }: IntentChipsProps) {
  const move = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    const step =
      e.key === "ArrowRight" || e.key === "ArrowDown"
        ? 1
        : e.key === "ArrowLeft" || e.key === "ArrowUp"
          ? -1
          : 0;
    if (!step) return;
    e.preventDefault();
    const next = (i + step + labels.length) % labels.length;
    onPick(next);
    const buttons = e.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>("button");
    buttons?.[next]?.focus();
  };
  return (
    <div
      role="radiogroup"
      aria-label="How to answer"
      data-intent-chips={cardId}
      className="flex flex-wrap items-center gap-2"
    >
      {labels.map((label, i) => (
        <button
          key={`${i}:${label}`}
          type="button"
          role="radio"
          aria-checked={i === index}
          tabIndex={i === index ? 0 : -1}
          disabled={disabled}
          onClick={() => onPick(i)}
          onKeyDown={(e) => move(e, i)}
          className={cn(
            "rounded-full border px-3 py-1 text-sm transition-colors",
            i === index
              ? "border-accent bg-accent-soft font-medium text-accent-text"
              : "border-line text-ink-2 hover:bg-sunk hover:text-ink",
            disabled && "opacity-70",
          )}
        >
          {label}
        </button>
      ))}
      <Kbd className="hidden sm:inline-flex">V</Kbd>
    </div>
  );
}
